/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable jsx-a11y/alt-text */
import * as React from "react";
import { Box } from "@mui/material";
import { TxtInput } from "../../../components";
import { AppAlert } from "../../../components/dialogue/Dialogue";
import { AdminUserModel } from "../../../redux/types";
import searchIcon from "../../../images/searchIcon.png";

import {
  PageContainer,
  ContentWrapper,
  HeaderSection,
  TitleRow,
  PageTitle,
  PageSubtitle,
  SearchInputBox,
  SearchIcon,
  TableWrapper,
  TableHeader,
  TableHeaderCell,
  TableRow,
  TableCell,
  PrimaryCell,
  StatusBadge,
  PaginationWrapper,
  PaginationText,
  EmptyState,
  EmptyStateText,
} from "../../../components/table/CommonTable.styled";

import styled from "@emotion/styled";

const ActionButton = styled.button`
  padding: 8px 16px;
  border: none;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s;
`;

const AddButton = styled(ActionButton)`
  background-color: #4b87ed;
  color: white;
  width: 100%;

  &:hover {
    background-color: #3a6fd8;
  }

  @media only screen and (min-width: 768px) {
    width: auto;
  }
`;

const EditButton = styled(ActionButton)`
  background-color: #e5e7eb;
  color: #374151;

  &:hover {
    background-color: #d1d5db;
  }
`;

const DeleteButton = styled(ActionButton)`
  background-color: #fee2e2;
  color: #dc2626;

  &:hover {
    background-color: #fecaca;
  }
`;

const ResetButton = styled(ActionButton)`
  background-color: #fef3c7;
  color: #92400e;

  &:hover {
    background-color: #fde68a;
  }
`;

interface UserTableProps {
  onTapAddUser?: Function;
  users: AdminUserModel[];
  title?: string;
  onTapEditUser?: Function;
  onTapResetPassword?: Function;
  onTapDeleteUser?: Function;
  permissions?: boolean;
}

export const UserTable: React.FC<UserTableProps> = ({
  onTapAddUser,
  users,
  title,
  onTapEditUser,
  onTapResetPassword,
  onTapDeleteUser,
  permissions = true,
}) => {
  const inputRef = React.useRef<HTMLInputElement>(null);
  const [page, setPage] = React.useState(0);
  const [rowsPerPage] = React.useState(10);
  const [keyword, setKeyword] = React.useState("");
  const [open, setOpen] = React.useState(false);
  const [deleteUser, setDeleteUser] = React.useState("");
  const [deleteId, setDeleteId] = React.useState(0);

  const filterUsers = () => {
    if (keyword.length > 0 && Array.isArray(users)) {
      return users.filter((item) => {
        const firstName = item.firstName?.toLowerCase() || "";
        const lastName = item.lastName?.toLowerCase() || "";
        const fullName = `${firstName} ${lastName}`;

        return (
          firstName.includes(keyword.toLowerCase()) ||
          lastName.includes(keyword.toLowerCase()) ||
          fullName.includes(keyword.toLowerCase())
        );
      });
    }
    return users;
  };

  const goToPreviousPage = () => {
    setPage((prevPage) => Math.max(prevPage - 1, 0));
  };

  const goToNextPage = () => {
    const totalPages = Math.ceil(filterUsers().length / rowsPerPage);
    setPage((prevPage) => Math.min(prevPage + 1, totalPages - 1));
  };

  function capitalizeFirstLetter(str: string) {
    if (!str) return "";
    let newString = str.toLowerCase();
    return newString.charAt(0).toUpperCase() + newString.slice(1);
  }

  const getRoleBadgeVariant = (role: string): "success" | "info" | "warning" | "default" => {
    switch (role?.toLowerCase()) {
      case "admin":
        return "info";
      case "manager":
        return "success";
      case "user":
        return "default";
      default:
        return "default";
    }
  };

  const filteredUsers = filterUsers();
  const paginatedUsers = filteredUsers.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );
  const totalPages = Math.ceil(filteredUsers.length / rowsPerPage);

  return (
    <PageContainer>
      <AppAlert
        open={open}
        title={`Delete ${deleteUser}`}
        message={"Are you sure you want to delete this user? This action cannot be undone."}
        onClose={() => {
          setOpen(false);
          setDeleteId(0);
          setDeleteUser("");
        }}
        onConfirm={() => {
          if (onTapDeleteUser && deleteId > 0) {
            onTapDeleteUser(deleteId);
          }
          setKeyword("");
          setDeleteId(0);
          setDeleteUser("");
          setOpen(false);
        }}
      />

      <ContentWrapper>
        <HeaderSection>
          <TitleRow>
            <PageTitle>{title || "Users"}</PageTitle>
          </TitleRow>
          <PageSubtitle>{filteredUsers.length} total users</PageSubtitle>
        </HeaderSection>

        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            justifyContent: "space-between",
            alignItems: { xs: "stretch", md: "center" },
            gap: 2,
            mb: 3,
          }}
        >
          <SearchInputBox>
            <SearchIcon
              src={searchIcon}
              style={{ margin: "0 12px" }}
              onClick={() => inputRef.current?.focus()}
            />
            <TxtInput
              placeholder="Search users..."
              onChange={setKeyword}
              inputRef={inputRef}
              background="white"
              width="100%"
            />
          </SearchInputBox>

          {onTapAddUser && permissions && (
            <AddButton onClick={() => onTapAddUser()}>+ Create New User</AddButton>
          )}
        </Box>

        <TableWrapper>
          <TableHeader>
            <TableHeaderCell width="30%">Full Name</TableHeaderCell>
            <TableHeaderCell width="20%">Role</TableHeaderCell>
            <TableHeaderCell width="20%">Password</TableHeaderCell>
            <TableHeaderCell width="30%">Actions</TableHeaderCell>
          </TableHeader>

          {filteredUsers.length === 0 ? (
            <EmptyState>
              <EmptyStateText>
                {keyword ? "No users found matching your search" : "No users found"}
              </EmptyStateText>
            </EmptyState>
          ) : (
            paginatedUsers.map((user) => (
              <TableRow key={user.id}>
                <PrimaryCell width="30%" mobileLabel="Name">
                  {capitalizeFirstLetter(user.firstName)} {capitalizeFirstLetter(user.lastName)}
                </PrimaryCell>
                <TableCell width="20%" mobileLabel="Role">
                  <StatusBadge variant={getRoleBadgeVariant(user.role)}>
                    {capitalizeFirstLetter(user.role)}
                  </StatusBadge>
                </TableCell>
                <TableCell width="20%" mobileLabel="Password">
                  {permissions && (
                    <ResetButton onClick={() => onTapResetPassword?.(user.email)}>
                      Reset Password
                    </ResetButton>
                  )}
                </TableCell>
                <TableCell width="30%">
                  {permissions && (
                    <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                      <EditButton onClick={() => onTapEditUser?.(user)}>Edit</EditButton>
                      <DeleteButton
                        onClick={() => {
                          setDeleteUser(`${user.firstName} ${user.lastName}`);
                          setDeleteId(user.id);
                          setOpen(true);
                        }}
                      >
                        Delete
                      </DeleteButton>
                    </Box>
                  )}
                </TableCell>
              </TableRow>
            ))
          )}

          {filteredUsers.length > 0 && totalPages > 1 && (
            <PaginationWrapper>
              <Box
                component="button"
                onClick={goToPreviousPage}
                disabled={page === 0}
                sx={{
                  px: 2,
                  py: 1,
                  border: "none",
                  borderRadius: "6px",
                  backgroundColor: page === 0 ? "#f3f4f6" : "#e5e7eb",
                  color: page === 0 ? "#9ca3af" : "#374151",
                  fontSize: 13,
                  fontWeight: 500,
                  cursor: page === 0 ? "not-allowed" : "pointer",
                  "&:hover": page === 0 ? {} : { backgroundColor: "#d1d5db" },
                }}
              >
                ← Prev
              </Box>
              <PaginationText>
                Page {page + 1} of {totalPages}
              </PaginationText>
              <Box
                component="button"
                onClick={goToNextPage}
                disabled={page >= totalPages - 1}
                sx={{
                  px: 2,
                  py: 1,
                  border: "none",
                  borderRadius: "6px",
                  backgroundColor: page >= totalPages - 1 ? "#f3f4f6" : "#e5e7eb",
                  color: page >= totalPages - 1 ? "#9ca3af" : "#374151",
                  fontSize: 13,
                  fontWeight: 500,
                  cursor: page >= totalPages - 1 ? "not-allowed" : "pointer",
                  "&:hover": page >= totalPages - 1 ? {} : { backgroundColor: "#d1d5db" },
                }}
              >
                Next →
              </Box>
            </PaginationWrapper>
          )}
        </TableWrapper>
      </ContentWrapper>
    </PageContainer>
  );
};
